import React from 'react';
import styled from 'styled-components';

import { type SearchMetadata } from '@column/api';

const MatchedText = styled.span`
  color: ${({ theme }) => theme.primary.background};
  font-weight: bold;
`;

export const renderHighlight = (text: string) => {
  const parts = text.split(/(<em>.*?<\/em>)/g);
  return (
    <span>
      {parts.map((part, i) => {
        const match = part.match(/^<em>(.*?)<\/em>$/);
        if (match) {
          return <MatchedText key={i}>{match[1]}</MatchedText>;
        }
        return part;
      })}
    </span>
  );
};

export const stripEmTags = (s: string) => s.replace(/<\/?em>/g, '');

// Returns true if any character in the last `tailLength` characters of the
// fragment's stripped value is inside an `<em>...</em>` span.
export const isFragmentTailHighlighted = (fragment: string, tailLength: number): boolean => {
  const stripped = stripEmTags(fragment);
  if (stripped.length < tailLength) return false;
  const tailStart = stripped.length - tailLength;
  let strippedPos = 0;
  let inEm = false;
  let i = 0;
  while (i < fragment.length) {
    if (fragment.startsWith('<em>', i)) {
      inEm = true;
      i += 4;
    } else if (fragment.startsWith('</em>', i)) {
      inEm = false;
      i += 5;
    } else {
      if (inEm && strippedPos >= tailStart) return true;
      strippedPos += 1;
      i += 1;
    }
  }
  return false;
};

export const getHighlightedField = (
  searchMetadata: SearchMetadata | undefined | null,
  ...fields: string[]
): string | undefined => {
  if (!searchMetadata?.highlights) return undefined;
  for (const field of fields) {
    const fragments = searchMetadata.highlights[field];
    if (fragments?.length) return fragments[0];
  }
  return undefined;
};

export type HighlightSuffixCandidate = { field: string; label: string };

export const getHighlightedSuffix = (
  searchMetadata: SearchMetadata | undefined | null,
  candidates: HighlightSuffixCandidate[]
): { label: string; fragment: string } | undefined => {
  const highlights = searchMetadata?.highlights;
  if (!highlights) return undefined;
  for (const { field, label } of candidates) {
    const fragments = highlights[field];
    if (fragments?.length) return { label, fragment: fragments[0] };
  }
  return undefined;
};
